import { useMutation, useQueryClient } from '@tanstack/react-query';

import { _deleteInvitation, _deleteMember } from '@/api/entity/mutations';
import { ENTITY_QUERY_KEYS } from '@/api/entity/queries';
import { optimisticDeleteTeamMember } from '@/api/fund/set-query-data';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { Modal, theme, Typography } from '@/shared/design-system';
import { useEntitySlice, useSelectedUserConfigSlice } from '@/stores/selectors';

import { CONFIRM_MODAL_MAX_WIDTH } from './confirm-modal.constants';
import { DIRECTOR_WARNING_BODY, DIRECTOR_WARNING_PREFIX } from './team-member-copy';

export const DELETE_MEMBER_TITLE = 'Are you sure you want to remove this team member?';
export const DELETE_DIRECTOR_TITLE = 'You are deleting a team member with the Director role.';
export const DELETE_MEMBER_BODY = 'Once removed, the user will need to be invited again to rejoin.';

export type TeamMemberDeleteProps = {
  memberId?: string | null;
  invitationId?: string | null;
  userId?: string | null;
  isDirector?: boolean;
  dataUserId?: string | null;
  entityId?: string | null;
};

export const TeamMemberDelete = () => {
  const { open, handleClose, props } = useGlobalModal('TEAM_MEMBER_DELETE_MODAL');
  const queryClient = useQueryClient();
  const { selectedEntityId, setSelectedEntityId, setSelectedUserRole } =
    useSelectedUserConfigSlice();
  const { stepForms, updateStepForm } = useEntitySlice();
  const { mutate: deleteMember } = useMutation({ mutationFn: _deleteMember });
  const { mutate: deleteInvitation } = useMutation({ mutationFn: _deleteInvitation });

  const entityId = props?.entityId || stepForms.id;
  const cacheEntityId = props?.entityId || selectedEntityId;

  const handleOnSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [ENTITY_QUERY_KEYS.entities, cacheEntityId],
    });

    optimisticDeleteTeamMember({
      entityId: cacheEntityId,
      memberId: props?.memberId,
      invitationId: props?.invitationId,
    });

    if (props?.dataUserId === props?.userId) {
      setSelectedUserRole('user');
      updateStepForm({ id: '' });
      setSelectedEntityId(null);
    }
    handleClose();
  };

  const handleDelete = () => {
    if (props?.memberId) {
      deleteMember({ entityId, memberId: props?.memberId }, { onSuccess: handleOnSuccess });
    } else if (props?.invitationId) {
      deleteInvitation(
        { entityId, invitationId: props?.invitationId },
        { onSuccess: handleOnSuccess },
      );
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={(isOpen) => !isOpen && handleClose()}
      showClose
      maxWidth={CONFIRM_MODAL_MAX_WIDTH}
      title={props?.isDirector ? DELETE_DIRECTOR_TITLE : DELETE_MEMBER_TITLE}
      description={props?.isDirector ? undefined : DELETE_MEMBER_BODY}
      primaryAction={{ label: 'Delete', color: 'destructive', onClick: handleDelete }}
      secondaryAction={{ label: 'Cancel', onClick: handleClose }}
    >
      {props?.isDirector ? (
        <Typography variant="body3" color={theme.colors.text.secondary}>
          <Typography as="span" variant="body3" color={theme.colors.text.error}>
            {DIRECTOR_WARNING_PREFIX}
          </Typography>{' '}
          {DIRECTOR_WARNING_BODY}
        </Typography>
      ) : (
        <></>
      )}
    </Modal>
  );
};
